import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { endpoints } from '../../constants/endpoints';
import { IAutocompleteDataRow } from '../../interfaces/autocomplete-data-row.interface';
import { IDrug } from '../../interfaces/drug.interface';
import { IDrugsFilterDrugItem } from '../../interfaces/drugs-filter-drug-item.interface';
import { IInventory } from '../../interfaces/inventory.interface';
import * as Act from './drugs.actions';
import { IDrugShort } from '../../interfaces/IDrugShort.interface';
import { ICustomDrug } from '../../interfaces/custom-drug-interface';
import { DrugsService } from '../../services/drugs.service';

export interface DrugsStateModel {
  loading: boolean;
  drugList: IDrug[];
  inventoryList: IInventory[];
  drugsFilterDrugItems: IDrugsFilterDrugItem[],
  dosageUnits: IAutocompleteDataRow[],
  dosageForms: IAutocompleteDataRow[],
  customDrugs: ICustomDrug[];
  drugs: IDrugShort[];
  isCompoundNdcInvalid: boolean;
}

@State<DrugsStateModel>({
  name: 'drugs',
})

@Injectable()
export class DrugsState {

  constructor(
    private http: HttpClient
    ) {
  }

  @Selector()
  static loading(state: DrugsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static drugList(state: DrugsStateModel): IDrug[] {
    return state.drugList;
  }

  @Selector()
  static inventoryList(state: DrugsStateModel): IInventory[] {
    return state.inventoryList;
  }

  @Selector()
  static drugsFilterDrugItems(state: DrugsStateModel): IDrugsFilterDrugItem[] {
    return state.drugsFilterDrugItems;
  }

  @Selector()
  static dosageUnits(state: DrugsStateModel): IAutocompleteDataRow[] {
    return state.dosageUnits;
  }

  @Selector()
  static dosageForms(state: DrugsStateModel): IAutocompleteDataRow[] {
    return state.dosageForms;
  }

  @Selector()
  static customDrugs(state: DrugsStateModel): ICustomDrug[] {
    return state.customDrugs;
  }

  @Selector()
  static drugs(state: DrugsStateModel): IDrugShort[] {
    return state.drugs;
  }

  @Selector()
  static isCompoundNdcInvalid(state: DrugsStateModel): boolean {
    return state.isCompoundNdcInvalid;
  }


  @Action(Act.GetDrugListFromSearch)
  getDrugListFromSearch(ctx: StateContext<DrugsStateModel>, {params}: Act.GetDrugListFromSearch): Observable<any> {
 
    ctx.patchState({loading: true});
    
    return this.http.get(endpoints.drug.search +'?all=false')
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            drugList: resp.value?.drugs || [],
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  // TODO: Consolidate this with inventory.state -- we don't need both.
  @Action(Act.GetInventoryListFromSearch)
  getInventoryListFromSearch(ctx: StateContext<DrugsStateModel>, {params}: Act.GetInventoryListFromSearch): Observable<any> {
    params.gpi = null;
    return this.http.post(endpoints.inventory.search, params)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            inventoryList: resp?.value?.inventory,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetDrugsForDrugsFilter)
  getDrugsForDrugsFilter(ctx: StateContext<DrugsStateModel>, {}: Act.GetDrugsForDrugsFilter): Observable<any> {
 
    ctx.patchState({loading: true});
    
    return this.http.get(endpoints.drug.getAllFromInventory)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            drugsFilterDrugItems: resp?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetDosageUnits)
  getDosageUnits(ctx: StateContext<DrugsStateModel>, {}: Act.GetDosageUnits): Observable<any> {
 
    ctx.patchState({loading: true});
    
    return this.http.get(endpoints.drug.getDosageUnits)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            dosageUnits: resp?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetDosageForms)
  getDosageForms(ctx: StateContext<DrugsStateModel>, {}: Act.GetDosageForms): Observable<any> {
 
    ctx.patchState({loading: true});
    
    return this.http.get(endpoints.drug.getDosageForms)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            dosageForms: resp?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.AddDosageUnit)
  addDosageUnit(ctx: StateContext<DrugsStateModel>, {name}: Act.AddDosageUnit): Observable<DrugsStateModel> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.drug.createDosageUnit, {name})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
            const data = [...ctx.getState().dosageUnits || []];
            data.unshift({id: responseData?.value, name: name});
            ctx.patchState({
              dosageUnits: data
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.AddDosageForm)
  addDosageForm(ctx: StateContext<DrugsStateModel>, {name}: Act.AddDosageForm): Observable<DrugsStateModel> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.drug.createDosageForm, {name})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
            const data = [...ctx.getState().dosageForms || []];
            data.unshift({id: responseData?.value, name: name});
            ctx.patchState({
              dosageForms: data
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetCustomDrugs)
  getCustomDrugs(ctx: StateContext<DrugsStateModel>, {}: Act.GetCustomDrugs): Observable<DrugsStateModel> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.drug.getCompounds)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if (responseData?.success) {
            ctx.patchState({
              customDrugs: responseData.value?.map(x => ({
                ...x,
                packageNdcs: DrugsService.removeDashesFromNdc(x)
              })),
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.IsCompoundNdcInvalid)
  isCompoundNdcInvalid(ctx: StateContext<DrugsStateModel>, {ndc}: Act.IsCompoundNdcInvalid): Observable<DrugsStateModel>  {
    ctx.patchState({isCompoundNdcInvalid: undefined});
    return this.http.get(endpoints.drug.isCompoundNdcInvalid + ndc)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            isCompoundNdcInvalid: responseData?.value
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  

  @Action(Act.SetDrugsLoading)
  setLoading(ctx: StateContext<DrugsStateModel>, {isLoading}: Act.SetDrugsLoading): void {
    ctx.patchState({loading: isLoading});
  }

  @Action(Act.ClearDrugsState)
  clearDrugsState(ctx: StateContext<DrugsStateModel>, {}: Act.ClearDrugsState): void {
    ctx.setState(null);
  }

  
  @Action(Act.SetDrugs)
  setDrugs(ctx: StateContext<DrugsStateModel>, {drugs}: Act.SetDrugs): void {
    ctx.patchState({drugs});
  }

  @Action(Act.AppendCustomDrug)
  appendCustomDrug(ctx: StateContext<DrugsStateModel>, {drug}: Act.AppendCustomDrug): void {
    if (!drug.id || !drug.packageNdc || !drug.friendlyName || !drug.packageNdcs?.length || isNaN(drug.packageSize)) {
      throw Error('Cannot append invalid drug');
    }
    ctx.patchState({customDrugs: [...ctx.getState().customDrugs, {
      ...drug, packageNdcs: DrugsService.removeDashesFromNdc(drug)
    }]});
  }

  @Action(Act.ClearDrugsFilter)
  clearDrugsFilter(ctx: StateContext<DrugsStateModel>, {}: Act.ClearDrugsFilter): void {
    ctx.patchState({drugsFilterDrugItems: []});
  }

}